import { useState } from 'react';
import { z } from 'zod';
import { useApolloClient } from '@apollo/client/react';

import { Button } from '@repo/ui/Button';
import { Form, SelectorInputField, TextInputField, TextAreaField } from '@repo/ui/Form';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@repo/ui/Sheet';
import { registerInput, useZodForm } from '@repo/hooks/useZodForm';

import GET_SWITCHES_FOR_ACCOUNT from '../gql/getSwitchesForAccount.gql';
import { CREATE_SWITCH_FOR_ACCOUNT, defaultSwitchInput } from '../utils/resolveSwitchId';

// Same choices the Forwarding tabs offer; custom headers/params are set up there.
// MMS can't be forwarded by email.
const PROTOCOLS = {
  sms: [
    { label: 'No forwarding', value: 'NONE' },
    { label: 'HTTP', value: 'HTTP' },
    { label: 'Email', value: 'EMAIL' },
  ],
  mms: [
    { label: 'No forwarding', value: 'NONE' },
    { label: 'HTTP', value: 'HTTP' },
  ],
} as const;

type Protocol = (typeof PROTOCOLS)['sms'][number]['value'];

const endpointIssue = (protocol: Protocol, endpoint: string | undefined) => {
  if (protocol === 'NONE') return null;
  if (!endpoint) return 'Enter an endpoint, or choose "No forwarding".';
  if (protocol === 'HTTP' && !z.string().url().safeParse(endpoint).success) {
    return 'Enter a valid URL, e.g. https://example.com/webhook.';
  }
  if (protocol === 'EMAIL' && !z.string().email().safeParse(endpoint).success) {
    return 'Enter a valid email address.';
  }
  return null;
};

export const createSwitchSchema = z
  .object({
    name: z.string().min(2, { message: 'Switch name must contain at least 2 characters.' }),
    notes: z.string().optional(),
    smsProtocol: z.enum(['NONE', 'HTTP', 'EMAIL']).default('NONE'),
    smsEndpoint: z.string().optional(),
    mmsProtocol: z.enum(['NONE', 'HTTP']).default('NONE'),
    mmsEndpoint: z.string().optional(),
  })
  .superRefine((values, ctx) => {
    for (const channel of ['sms', 'mms'] as const) {
      const message = endpointIssue(values[`${channel}Protocol`], values[`${channel}Endpoint`]);
      if (message) ctx.addIssue({ code: 'custom', path: [`${channel}Endpoint`], message });
    }
  });

export type CreateSwitchValues = z.infer<typeof createSwitchSchema>;

/**
 * The createSwitchForAccount input. A channel with an endpoint is created with
 * forwarding on; one without keeps the defaults (off), since the backend
 * rejects enabled switches that have no real endpoint.
 */
export const buildCreateSwitchInput = (values: CreateSwitchValues) => {
  const input: Record<string, unknown> = {
    ...defaultSwitchInput(values.name),
    name: values.name,
    notes: values.notes ?? '',
  };
  for (const channel of ['sms', 'mms'] as const) {
    const protocol = values[`${channel}Protocol`];
    const endpoint = values[`${channel}Endpoint`]?.trim();
    if (protocol !== 'NONE' && endpoint) {
      input[`${channel}Enabled`] = true;
      input[`${channel}Protocol`] = protocol;
      input[`${channel}Endpoints`] = [endpoint];
    }
  }
  return input;
};

const ChannelFields = ({
  form,
  channel,
}: {
  form: ReturnType<typeof useZodForm<typeof createSwitchSchema>>;
  channel: 'sms' | 'mms';
}) => {
  const label = channel.toUpperCase();
  const protocol = form.watch(`${channel}Protocol`);

  return (
    <div className="flex flex-col gap-4 pt-4 border-t sm:col-span-2 border-grey-400 dark:border-grey-700">
      <span className="text-sm font-bold">{label} forwarding</span>
      <SelectorInputField
        label={`${label} protocol`}
        options={PROTOCOLS[channel] as unknown as { label: string; value: string }[]}
        {...registerInput(form, `${channel}Protocol`)}
      />
      {protocol !== 'NONE' && (
        <TextInputField
          label={`${label} endpoint`}
          placeholder={protocol === 'EMAIL' ? 'name@example.com' : 'https://example.com/webhook'}
          {...registerInput(form, `${channel}Endpoint`)}
        />
      )}
    </div>
  );
};

const CreateSwitchForm = ({
  accountId,
  onCreated,
}: {
  accountId: string;
  onCreated: () => void;
}) => {
  const client = useApolloClient();

  const form = useZodForm({
    schema: createSwitchSchema,
    onSubmit: async (values) => {
      await client.mutate({
        mutation: CREATE_SWITCH_FOR_ACCOUNT,
        variables: { accountId, input: buildCreateSwitchInput(values) },
      });
      onCreated();
    },
  });

  return (
    <Form {...form} column={1}>
      <Form.Error>Unable to create switch. Please try again later or contact support.</Form.Error>
      <TextInputField id="name" label="Name" {...form.register('name')} />
      <TextAreaField id="notes" label="Notes" {...form.register('notes')} />
      <ChannelFields form={form} channel="sms" />
      <ChannelFields form={form} channel="mms" />
      <p className="text-sm sm:col-span-2 text-grey-600 dark:text-grey-400">
        Custom headers and parameters can be added on the switch&apos;s Forwarding tab.
      </p>
      <div className="sm:col-span-2">
        <Button type="submit">Create Switch</Button>
      </div>
    </Form>
  );
};

/** "Create Switch" button that opens the create form in a side panel. */
const CreateSwitchSheet = ({ accountId }: { accountId: string }) => {
  const [open, setOpen] = useState(false);
  const client = useApolloClient();

  // Refresh the account's switch list, then close the panel
  const handleCreated = async () => {
    await client.refetchQueries({ include: [GET_SWITCHES_FOR_ACCOUNT] });
    setOpen(false);
  };

  return (
    <>
      <Button type="button" onClick={() => setOpen(true)}>
        Create Switch
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="overflow-y-auto w-full sm:max-w-lg">
          <SheetHeader className="border-b border-border">
            <SheetTitle>Create Switch</SheetTitle>
          </SheetHeader>
          <div className="px-5 mt-6 w-full">
            {open && <CreateSwitchForm accountId={accountId} onCreated={handleCreated} />}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default CreateSwitchSheet;
